import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  BASE_URL,
  DETAILED_LOYALTY_POINTS,
  DETAILED_LOYALTY_POINTS_COUNT,
} from "../../../../Constants/Config";

const initialState = {
  loading: false,
  DetailedLoyaltyPointsReportArr: [],
  DetailedLoyaltyPointsReportTotals: {
    total_credit_points: 0,
    total_debit_points: 0,
    total_available_lp: 0,
  },
  status: false,
  successMessage: "",
  error: "",
  offset: 0,
  limit: 10,
  hasMore: true,

  countLoading: false,
  countData: 10,
  latestRequestId: null, // Store the latest request ID
};

let loyaltyPointCountSource = null;
let loyaltyPointsController = null;

export const fetchDetailedLoyaltyPointsReportArr = createAsyncThunk(
  "DetailedLoyaltyPointsReport/fetchDetailedLoyaltyPointsReportArr",
  async (data, { rejectWithValue, requestId }) => {
    // if (cancelTokenSource) {
    //   cancelTokenSource.cancel("Previous request canceled");
    // }
    if (loyaltyPointsController) {
      loyaltyPointsController.abort();
    }
    // Create a new cancel token
    // cancelTokenSource = axios.CancelToken.source();
    loyaltyPointsController = new AbortController();
    // const controller = new AbortController();
    const timeout = setTimeout(() => loyaltyPointsController.abort(), 45000);
    try {
      const { token, ...dataNew } = data;
      const res = await axios.post(
        BASE_URL + DETAILED_LOYALTY_POINTS,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          // cancelToken: cancelTokenSource.token,
          signal: loyaltyPointsController.signal,
        }
      );
      clearTimeout(timeout);
      if (res.data.status === true) {
        const arr = res.data.loyalty_points_data;
        const status = res.data.status;
        const totalCustomerCount = res.data.total_count;
        const totalCreditPoints = res.data.total_credit_points;
        const totalDebitPoints = res.data.total_debit_points;
        const totalAvailableLP = res.data.total_available_lp;

        return { arr, status, totalCustomerCount, totalCreditPoints, totalDebitPoints, totalAvailableLP, requestId };
      } else if (res.data.status === false) {
        const arr = [];
        const status = false;
        const totalCustomerCount = 0;
        const totalCreditPoints = 0;
        const totalDebitPoints = 0;
        const totalAvailableLP = 0;
        return { arr, status, totalCustomerCount, totalCreditPoints, totalDebitPoints, totalAvailableLP };
      }
    } catch (error) {
      clearTimeout(timeout); // always clear timeout on error
      const isCanceled =
        axios.isCancel(error) ||
        error.name === "CanceledError" ||
        error.name === "AbortError";
      const customError = {
        message: "Request canceled",
        status: "canceled",
        data: null,
      };
      return rejectWithValue(
        isCanceled
          ? customError
          : {
            message: toThunkRejectValue(error).message,
            status: toThunkRejectValue(error).status,
            data: error.response ? error.response.data : null,
          }
      );
    }
  }
);

export const fetchDetailedLoyaltyPointsReportCount = createAsyncThunk(
  "products/fetchDetailedLoyaltyPointsReportCount",
  async (payload, { rejectWithValue }) => {
    if (loyaltyPointCountSource) {
      loyaltyPointCountSource.cancel("Previous request canceled");
    }
    // Create a new cancel token
    loyaltyPointCountSource = axios.CancelToken.source();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000);

    const { token, ...dataNew } = payload;
    try {
      const response = await axios.post(
        BASE_URL + DETAILED_LOYALTY_POINTS_COUNT,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          cancelToken: loyaltyPointCountSource.token,
          signal: controller.signal,
        }
      );
      clearTimeout(timeout);
      if (response?.data?.status) {
        return response?.data?.loyalty_points_count;
      }
    } catch (error) {
      clearTimeout(timeout);
      const isCanceled =
        axios.isCancel(error) ||
        error.name === "CanceledError" ||
        error.name === "AbortError";
      if (isCanceled) {
        // console.log("Request canceled:", error.message);
      } else {
        const customError = {
          message: isCanceled ? "Request canceled" : error.message,
          status: isCanceled
            ? "canceled"
            : error.response
              ? error.response.status
              : "Network Error",
          data: error.response ? error.response.data : null,
        };
        return rejectWithValue(customError);
      }
    }
  }
);

const DetailedLoyaltyPointsReportSlice = createSlice({
  name: "DetailedLoyaltyPointsReport",
  initialState,
  reducers: {
    emptyLoyaltyReportData: (state, action) => {
      state.loading = false;
      state.DetailedLoyaltyPointsReportArr = [];
      state.DetailedLoyaltyPointsReportTotals = {
        total_credit_points: 0,
        total_debit_points: 0,
        total_available_lp: 0,
      };
      state.status = false;
      state.successMessage = "";
      state.error = "";
      state.offset = 0;
      state.limit = 10;
      state.hasMore = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchDetailedLoyaltyPointsReportArr.pending,
      (state, action) => {
        state.loading = true;
        state.DetailedLoyaltyPointsReportArr = [];
        state.DetailedLoyaltyPointsReportTotals = {
          total_credit_points: 0,
          total_debit_points: 0,
          total_available_lp: 0,
        };
        state.latestRequestId = action.meta.requestId; // Store latest request ID
      }
    );
    builder.addCase(
      fetchDetailedLoyaltyPointsReportArr.fulfilled,
      (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.status = action.payload.status;
        state.DetailedLoyaltyPointsReportArr = action.payload.arr;
        state.DetailedLoyaltyPointsReportTotals = {
          total_credit_points: action.payload.totalCreditPoints,
          total_debit_points: action.payload.totalDebitPoints,
          total_available_lp: action.payload.totalAvailableLP,
        };
        state.error = "";
        // state.countData = action.payload.totalCustomerCount;
        // state.hasMore =
        //   +action?.payload?.totalCustomerCount >
        //   +state?.DetailedLoyaltyPointsReportArr?.length;

        // if (
        //   +action?.payload?.totalCustomerCount >
        //   +state?.DetailedLoyaltyPointsReportArr?.length
        // ) {
        //   state.limit += 10; // Increment only if there are new products
        // }
        // if (Array.isArray(action?.payload?.arr)) {
        //   state.DetailedLoyaltyPointsReportArr = action.payload.arr;
        // }
      }
    );
    builder.addCase(
      fetchDetailedLoyaltyPointsReportArr.rejected,
      (state, action) => {
        // if (state.latestRequestId !== action.payload?.requestId) {
        //   return; // Ignore outdated errors
        // }
        if (state.latestRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.DetailedLoyaltyPointsReportArr = [];
        state.DetailedLoyaltyPointsReportTotals = {
          total_credit_points: 0,
          total_debit_points: 0,
          total_available_lp: 0,
        };
        state.error = action.error.message;
        // console.log("DetailedLoyaltyPointsReportArr Error: ", state.loading);
      }
    );

    builder.addCase(fetchDetailedLoyaltyPointsReportCount.pending, (state) => {
      state.countLoading = true;
      state.countData = [];
    });
    builder.addCase(
      fetchDetailedLoyaltyPointsReportCount.fulfilled,
      (state, action) => {
        state.countLoading = false;
        state.countData = +action.payload;
      }
    );
    builder.addCase(
      fetchDetailedLoyaltyPointsReportCount.rejected,
      (state, action) => {
        state.countLoading = false;
        state.countData = [];
      }
    );
  },
});

export const { emptyLoyaltyReportData } =
  DetailedLoyaltyPointsReportSlice.actions;

export default DetailedLoyaltyPointsReportSlice.reducer;
